import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/forms/FormField';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { NumberPreview } from './_shared';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { payrollSettingsSchema, type PayrollSettingsFormData } from '@/lib/validators';
import type { OrganizationSettings } from '@/types/table';

/** Preview of the next auto-generated employee code (EMP-0001). */
function CodePreview({ prefix, seq }: { prefix: string; seq: number }) {
  const safePrefix = (prefix || '').trim() || 'EMP';
  const code = `${safePrefix}-${String(Math.max(1, Math.floor(seq || 1))).padStart(4, '0')}`;
  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
      <span className="text-muted-foreground">Next employee code:</span>
      <span className="ml-2 font-mono font-medium text-foreground">{code}</span>
    </div>
  );
}

/**
 * Payroll tab — the labels printed on payslips for the employee's statutory
 * identifiers, plus employee-code numbering.
 *
 * The Employee columns themselves are jurisdiction-neutral (taxIdentifier,
 * socialSecurityId, pensionId); these labels are what an org actually sees, so
 * a deployment outside India renames them rather than living with PAN/UAN.
 */
export function PayrollTab() {
  const { settings, update } = useOrganizationSettings();

  const form = useForm<PayrollSettingsFormData>({
    resolver: zodResolver(payrollSettingsSchema),
    defaultValues: {
      taxIdentifierLabel: 'PAN',
      socialSecurityIdLabel: 'ESI Number',
      pensionIdLabel: 'UAN',
      employeeCodePrefix: 'EMP',
      nextEmployeeSequence: 1,
      payslipPrefix: 'PAY',
      nextPayslipSequence: 1,
      payslipFooterText: '',
    },
  });

  useEffect(() => {
    if (!settings) return;
    form.reset({
      taxIdentifierLabel: settings.taxIdentifierLabel ?? 'PAN',
      socialSecurityIdLabel: settings.socialSecurityIdLabel ?? 'ESI Number',
      pensionIdLabel: settings.pensionIdLabel ?? 'UAN',
      employeeCodePrefix: settings.employeeCodePrefix ?? 'EMP',
      nextEmployeeSequence: settings.nextEmployeeSequence ?? 1,
      payslipPrefix: settings.payslipPrefix ?? 'PAY',
      nextPayslipSequence: settings.nextPayslipSequence ?? 1,
      payslipFooterText: settings.payslipFooterText ?? '',
    });
  }, [settings, form]);

  const values = form.watch();
  const onSubmit = (data: PayrollSettingsFormData) => update.mutate(data as Partial<OrganizationSettings>);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Statutory identifier labels</CardTitle>
            <CardDescription>
              What each employee identifier is called on the payslip. Rename these for your jurisdiction —
              the stored values are neutral, only the labels change.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField form={form} name="taxIdentifierLabel" label="Tax identifier" required
              description="India: PAN · US: SSN · UK: NI Number · Australia: TFN" />
            <FormField form={form} name="socialSecurityIdLabel" label="Social security identifier" required
              description="India: ESI Number · US: — · UK: NI Number" />
            <FormField form={form} name="pensionIdLabel" label="Pension identifier" required
              description="India: UAN · elsewhere: pension / retirement account number" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Employee numbering</CardTitle>
            <CardDescription>
              Codes are issued as {'{prefix}-{sequence}'}. Unlike invoice numbers these are not
              year-scoped — an employee code should outlive the year it was issued in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField form={form} name="employeeCodePrefix" label="Prefix" required placeholder="EMP" />
            <FormField form={form} name="nextEmployeeSequence" label="Next sequence" type="decimal" required
              description="Auto-bumps after each employee is created. Override to skip ahead." />
            <CodePreview prefix={values.employeeCodePrefix} seq={values.nextEmployeeSequence} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payslip document</CardTitle>
          <CardDescription>
            Payslip numbers follow the same shape as your other documents:
            {' '}{'{company}-{marker}-{year}-{sequence}'}.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <FormField form={form} name="payslipPrefix" label="Payslip marker" required placeholder="PAY" />
            <FormField form={form} name="nextPayslipSequence" label="Next sequence" type="decimal" required
              description="Auto-bumps after each payslip is generated." />
            <NumberPreview
              kind="Payslip"
              company={settings?.companyPrefix ?? ''}
              marker={values.payslipPrefix}
              seq={values.nextPayslipSequence}
            />
          </div>
          <FormTextarea form={form} name="payslipFooterText" label="Footer text" rows={4} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pay components</CardTitle>
          <CardDescription>
            Earnings, deductions and employer contributions are managed on their own page — they are
            shared by every salary structure.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button type="button" variant="outline" asChild>
            <Link to="/people/pay-components">
              Manage pay components<ExternalLink className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save payroll settings'}
        </Button>
      </div>
    </form>
  );
}
